<template>
  <div>
    <div class="namespace-admins-page" v-if="!isLoading">
      <DeleteNamespaceAdminModal
        ref="$deleteAdminModal"
        :admins="selectedAdminUsers"
        :project-id="projectId"
        :namespace-id="namespaceId"
        @onSuccessDelete="onSuccessDelete"
      />

      <h2 class="namespace-admins-page__title">Администраторы</h2>
      <div class="namespace-admins-page__content">
        <TirPmSearchInput
          style="width: 100%"
          label="Администраторы"
          placeholder=""
          :isWithPopup="true"
          v-model="searchQueryUsers"
          @input="filteredUser"
          :isDisabled="!isOwner && !isAdminProject"
        >
          <template #content>
            <TirPmSearchInputContentLine
              v-for="user of copiComparedUserList"
              :is-disabled="user.IsProjectAdmin"
              :key="user.Id"
              @mousedown="onMouseDown(user, $event)"
              @click.stop="setAdminToProject(user)"
            >
              <template #leftSlot>
                <TirPmIconView
                  :size="24"
                  isRounded
                  :isBordered="false"
                  :colorCode="user.AvatarColor"
                  :imageLoadUrl="user.Photo"
                >
                  <template #mainSlot>
                    {{ user?.FirstName?.[0].toUpperCase() }}{{ user?.LastName?.[0].toUpperCase() }}
                  </template>
                </TirPmIconView>
              </template>

              <span>{{ user.FirstName }} {{ user.MiddleName }} {{ user.LastName }}</span>
              <span v-if="user.IsProjectAdmin"> (Администратор проекта)</span>
              <template #rightSlot></template>
            </TirPmSearchInputContentLine>
          </template>
        </TirPmSearchInput>
      </div>
      <div class="namespace-admins-page__users-list">
        <div
          class="namespace-admins-page__user-item"
          v-for="adminUser of sortAdmins"
          :key="adminUser.Id"
        >
          <div class="namespace-admins-page__user-item-left">
            <TirPmIconView
              :size="48"
              isRounded
              :isBordered="false"
              :colorCode="adminUser.AvatarColor"
              :imageLoadUrl="adminUser.Photo"
            >
              <template #mainSlot>
                {{ adminUser?.FirstName?.[0].toUpperCase()
                }}{{ adminUser?.LastName?.[0].toUpperCase() }}
              </template>
            </TirPmIconView>
            <div class="namespace-admins-page__sign-name">
              {{ adminUser?.FirstName }} {{ adminUser?.MiddleName }} {{ adminUser?.LastName }}
            </div>
          </div>
          <div class="namespace-admins-page__user-item-right">
            <TirPmButton
              class="namespace-admins-page__delete-button"
              v-if="isOwner || isAdminProject"
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
              @click="$deleteAdminModal?.open(adminUser)"
            >
              <template #leftSlot>
                <TrashIcon />
              </template>
            </TirPmButton>
            <TirPmTooltipElement>
              <TirCheckbox
                :is-disabled="!(isOwner || rightsFormQueue)"
                :model-value="adminUser.CanCustomizeOrder"
                @update:model-value="updateCanCustomizeOrder(adminUser, $event)"
              />
              <template #content>
                <div class="namespace-admins-page__checkbox-tooltip-content">
                  Может формировать очередность задач
                </div>
              </template>
            </TirPmTooltipElement>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, inject, ComputedRef, watch } from 'vue';
import { AdapterService } from './adapter';
import { SaveAdminsModel, UserModel } from './models';
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmIconView,
  TirPmNotificationModel,
  TirPmNotificationEventBus,
  TirPmModal,
  TirCheckbox,
  TirPmPopover,
  PopoverColorEnum,
  PopoverPositionEnum,
  TirPmTooltipElement,
} from 'tir-components';
import { TirPmSearchInput, TirPmSearchInputContentLine } from 'tir-pm-search-input';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { TrashIcon } from 'tir-style-system/icons/outline';
import { onMounted } from 'vue';
import ShortProfile from '@/api/Main/dataSource/api/users/profile/i-service/models/ShortProfile';
import { useRouter } from 'vue-router';
import { DeleteNamespaceAdminModal } from './components';

const props = defineProps({
  /**ID неймспейса */
  namespaceId: {
    type: String,
    required: true,
  },
  /**ID проекта */
  projectId: {
    type: Number,
    required: true,
  },
});

const route = useRouter();

const adapter = new AdapterService();

const checkRightsFormQueue = ref();
/**Ссылка на модальное окно удаления админа неймспейса */
const $deleteAdminModal = ref<InstanceType<typeof DeleteNamespaceAdminModal>>();

/**Владелец */
const isOwner = computed(() => !!CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isAdminProject = inject('isAdminProject') as ComputedRef<boolean>;

const rightsFormQueue = ref();

/**Загузка */
const isLoading = ref(false);

/**Получить список админов неймспейса */
const getNamespaceAdmins = async () => {
  copiedEditData.value = (await adapter.getNamespaceAdmins(props.namespaceId)).Value;
  selectedAdminUsers.value = copiedEditData.value;
};

const onSuccessDelete = async () => {
  await getNamespaceAdmins();
  filteredUser();
};

const searchQueryUsers = ref<string>(null);

// Сохранить модалку редактирования
const saveModalEditEmployee = async (users: Array<UserModel>) => {
  try {
    const resp = await adapter.saveNamespaceAdmins(
      new SaveAdminsModel({
        NamespaceId: props.namespaceId,
        Admins: users.map((admin) => ({
          Id: admin.Id,
          CanCustomizeOrder: admin.CanCustomizeOrder,
        })),
      }),
    );
    if (resp.IsSuccess) {
      await getNamespaceAdmins();
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Успешное редактирование',
          type: 'success',
          group: 'general',
          life: 5000,
          closable: true,
          callbackButton: () => {},
        }),
      );
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: resp.ErrorMessage ?? 'Произошла ошибка',
          type: 'error',
          group: 'general',
          life: 5000,
          closable: true,
          callbackButton: () => {},
        }),
      );
    }
  } catch (err: any) {
    console.log(err, 'error new');
    TirPmNotificationEventBus.emit(
      'add',
      new TirPmNotificationModel({
        title: err.ErrorMessage,
        type: 'error',
        group: 'general',
        life: 5000,
        closable: true,
        callbackButton: () => {},
      }),
    );
  }
};

const copiedEditData = ref();

// Переменная для списка пользователей, чтобы назначить из него админом или участником для селекта
const UserList = ref<Array<UserModel>>([]);

const selectedAdminUsers = ref<Array<UserModel>>([]);
const copiComparedUserList = ref<Array<UserModel>>([]);

/**Сортировка админов по имени */
const sortAdmins = computed(() =>
  selectedAdminUsers.value.sort((a, b) => a.FirstName.localeCompare(b.FirstName)),
);

// Функция для фильтрации юзеров
const filteredUser = (selectedUserName?: UserModel & string) => {
  copiComparedUserList.value = UserList.value;
  copiComparedUserList.value = copiComparedUserList.value.filter(
    (x) => !selectedAdminUsers.value.find((y) => y.Id == x.Id),
  );
  copiComparedUserList.value.sort((a, b) => a.FirstName.localeCompare(b.FirstName));
  if (typeof selectedUserName == 'string' && selectedUserName.length) {
    copiComparedUserList.value = copiComparedUserList.value.filter((x) => {
      let compairedString = x?.FirstName + ' ' + x?.MiddleName + ' ' + x?.LastName;
      return compairedString.toLowerCase().includes(selectedUserName.toLowerCase());
    });
  }
};

// Функция для выбора юзера для селекта администраторы
const setAdminToProject = async (selectedUser: UserModel) => {
  if (selectedUser.IsProjectAdmin || selectedUser.IsNamespaceAdmin) return;

  await saveModalEditEmployee([...selectedAdminUsers.value, selectedUser]);
  filteredUser();
  searchQueryUsers.value = '';
};

const onMouseDown = (user: UserModel, event: MouseEvent) => {
  if (user.IsProjectAdmin || user.IsNamespaceAdmin) event.preventDefault();
};

/**
 * Обновить canCustomizeOrder у пользователя
 */
const updateCanCustomizeOrder = async (user: UserModel, canCustomizeOrder: boolean) => {
  const index = selectedAdminUsers.value.findIndex((_user) => _user.Id === user.Id);
  if (index === -1) return;

  const updateUsers = [...selectedAdminUsers.value];

  updateUsers[index] = new UserModel({
    ...user,
    CanCustomizeOrder: canCustomizeOrder,
  });

  await saveModalEditEmployee(updateUsers);
};

// Функция для удаления юзера для селекта администраторы
// const removeAdminForProject = () => {
//   if (adminToDelete.value === undefined) return;
//   selectedAdminUsers.value = selectedAdminUsers.value.filter((x) => x.Id !== adminToDelete.value);
//   filteredUser();
//   saveModalEditEmployee();
//   adminToDelete.value = undefined;
// };

const loadProjects = async () => {
  let resp = await adapter.getEmployees(props.projectId, props.namespaceId);

  UserList.value = resp.Value.map(
    (x) =>
      new UserModel({
        Id: x.Id,
        FirstName: x.FirstName,
        LastName: x.LastName,
        MiddleName: x.MiddleName,
        Photo: x.Photo,
        AvatarColor: x.AvatarColor,
        IsProjectAdmin: x.IsProjectAdmin,
        IsNamespaceAdmin: x.IsNamespaceAdmin,
      }),
  );
  copiComparedUserList.value = UserList.value;
  filteredUser();
};

onMounted(async () => {
  const result = await adapter.checkProjectAdmin(
    Number(route.currentRoute.value.params.projectId),
    CurrentUser.Id,
  );
  checkRightsFormQueue.value = (await result).Value;
});

watch(
  () => checkRightsFormQueue.value,
  () => {
    rightsFormQueue.value = checkRightsFormQueue.value.CanCustomizeOrder;
  },
);

onMounted(async () => {
  try {
    copiedEditData.value = (await adapter.getNamespaceAdmins(props.namespaceId)).Value;
    selectedAdminUsers.value = copiedEditData.value;
    await loadProjects();
  } catch {}
});
</script>

<style lang="scss" scoped>
@use 'sass:map';
@import 'tir-style-system/expose-variables';
@import 'tir-style-system/expose-default-theme';
@import 'tir-style-system/core/mixins';

.namespace-admins-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  &__content-line {
    background-color: red;
  }
  &__title {
    display: block;
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.25rem;
    margin-top: 0;
    margin-bottom: 1rem;
  }
  &__button {
    margin-top: 3rem;
  }
  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  &__controls {
    margin-top: 3rem;
  }

  &__user-item {
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: background-color 0.2s ease-in;
    border-radius: 0.5rem;
  }

  &__user-item:hover {
    background-color: var(--neutral-translucent-5, map.get($colors, 'neutral-translucent-5'));
  }

  &__user-item-left {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__sign-name {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--text-primary, map.get($colors, 'text-primary'));
  }

  &__delete-button {
    visibility: hidden;
  }

  &__user-item-right {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__user-item:hover &__delete-button {
    visibility: visible;
  }

  &__checkbox-tooltip-content {
    display: flex;
    justify-content: center;
    overflow-wrap: break-word;
    border-radius: 0.5rem;
    color: var(--text-primary-inverted, #e9eaeb);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
  }
}
</style>
<style lang="scss">
.namespace-admins-page__user-item-right {
  .button__left-slot {
    width: 1.5rem !important;
    height: 1.5rem !important;
  }
}
</style>
